import React, { useCallback, useEffect, useMemo, useState } from "react";
import LogParsingWorker from "../../utils/log-parsing-worker.worker?worker";
import { CustomFab } from "./components";
import { Box, Zoom } from "@mui/material";
import { North, South } from "@mui/icons-material";
import { styled as materialStyled } from "@mui/material/styles";
import { DateTime } from "luxon";
import { atom, useAtom } from "jotai/index";
import {
  DeviceLogsGrid,
  type NormalizedLogRow,
} from "@/components/test-editor-gpt/device-logs/DeviceLogs";
import { useGridApiRef } from "@mui/x-data-grid-pro";

interface ParsedLog {
  type: string;
  level?: string;
  process?: string;
  time: number;
  message: string;
  relativeTimestamp: number | null;
}

interface ConsoleTabProps {
  consoleLogs: any[];
  playbackTimestamp: number;
  display: boolean;
  logsUrl?: string;
  platform: string;
  startedTimeInEpoch?: number;
}

const ConsoleTab = ({
  consoleLogs,
  playbackTimestamp,
  display,
  logsUrl,
  platform,
  startedTimeInEpoch,
}: ConsoleTabProps) => {
  const [parsedLogs, setParsedLogs] = useState<ParsedLog[] | null>(null);
  const apiRef = useGridApiRef();

  const [forceMoveToTimestampDeviceTab, setForceMoveToTimestampDeviceTab] =
    useAtom(forceMoveToTimestampDeviceTabAtom);

  useEffect(() => {
    const worker = new LogParsingWorker();

    worker.onmessage = (event) => {
      setParsedLogs(event.data);
    };

    worker.onerror = (error) => {
      console.error("Web Worker error:", error);
    };

    worker.postMessage({
      data: consoleLogs,
      isIos: platform.toLowerCase() === "ios",
      startedTimeInEpoch,
    });

    return () => {
      worker.terminate();
    };
  }, [consoleLogs, platform, startedTimeInEpoch]);

  const rows = useMemo<NormalizedLogRow[]>(() => {
    if (!parsedLogs) return [];
    return parsedLogs.map((log, index) => ({
      id: index,
      timestamp:
        log.relativeTimestamp != null
          ? DateTime.fromMillis(log.time).toFormat("hh:mm:ss")
          : "-",
      level: log.level,
      message: log.message,
      process: log.process,
      relativeTimestamp: log.relativeTimestamp,
    }));
  }, [parsedLogs]);

  const currentTimestampIndex = useMemo(() => {
    return rows.findLastIndex(
      (row) =>
        row.relativeTimestamp != null &&
        row.relativeTimestamp <= playbackTimestamp
    );
  }, [rows, playbackTimestamp]);

  useEffect(() => {
    if (forceMoveToTimestampDeviceTab == null) return;
    // Don't consume the atom until the worker has finished — otherwise rows is
    // empty, findLastIndex returns -1, and the atom gets cleared prematurely.
    if (parsedLogs === null) return;

    const timestamp = forceMoveToTimestampDeviceTab;
    const indexToJump = rows.findLastIndex(
      (row) =>
        row.relativeTimestamp != null && timestamp >= row.relativeTimestamp
    );

    setForceMoveToTimestampDeviceTab(null);

    if (indexToJump < 0) return;

    // DataGrid is always mounted (display:none when hidden) so apiRef is valid.
    apiRef.current?.scrollToIndexes({ rowIndex: indexToJump });
  }, [
    forceMoveToTimestampDeviceTab,
    parsedLogs,
    rows,
    setForceMoveToTimestampDeviceTab,
  ]);

  const [jumpToTimestamp, setJumpToTimestamp] = useState<
    "hidden" | "top" | "bottom"
  >("hidden");

  const handleScrollChange = useCallback(() => {
    if (!apiRef.current || rows.length === 0) {
      setJumpToTimestamp("hidden");
      return;
    }

    const renderContext = apiRef.current.state?.virtualization?.renderContext;
    if (!renderContext) {
      setJumpToTimestamp("hidden");
      return;
    }

    const firstVisibleIndex = renderContext.firstRowIndex ?? 0;
    const lastVisibleIndex = renderContext.lastRowIndex ?? 0;

    if (currentTimestampIndex < 0) {
      setJumpToTimestamp("hidden");
    } else if (currentTimestampIndex < firstVisibleIndex) {
      setJumpToTimestamp("top");
    } else if (currentTimestampIndex > lastVisibleIndex) {
      setJumpToTimestamp("bottom");
    } else {
      setJumpToTimestamp("hidden");
    }
  }, [rows.length, currentTimestampIndex]);

  useEffect(() => {
    handleScrollChange();
  }, [playbackTimestamp, handleScrollChange]);

  // Subscribe to DataGrid scroll events so the FAB appears when the user manually scrolls
  useEffect(() => {
    if (!display) return;
    return apiRef.current?.subscribeEvent(
      "scrollPositionChange",
      handleScrollChange
    );
  }, [display, handleScrollChange]);

  const jumpToTimestampHandler = useCallback(() => {
    if (currentTimestampIndex >= 0 && apiRef.current) {
      apiRef.current.scrollToIndexes({ rowIndex: currentTimestampIndex });
      setJumpToTimestamp("hidden");
    }
  }, [currentTimestampIndex]);

  if (logsUrl) {
    if (!display) return null;
    return (
      <div className="flex justify-center pt-4">
        <a href={logsUrl} target="_blank" rel="noreferrer">
          Open browserstack logs
        </a>
      </div>
    );
  }

  // Keep the DataGrid always mounted (CSS-hidden when not displayed) so that
  // apiRef stays valid and scrollToIndexes works when jumping from another tab.
  return (
    <ConsoleLogsTableWrapper sx={{ display: display ? "flex" : "none" }}>
      <Zoom in={jumpToTimestamp !== "hidden"} unmountOnExit>
        <CustomFab
          variant="extended"
          jumpToTimestamp={jumpToTimestamp}
          onClick={jumpToTimestampHandler}
        >
          {jumpToTimestamp !== "top" ? <South /> : <North />}
          Jump to current timestamp
        </CustomFab>
      </Zoom>
      <DeviceLogsGrid
        rows={rows}
        loading={parsedLogs == null}
        playbackTimestamp={playbackTimestamp}
        currentTimestampIndex={currentTimestampIndex}
        apiRef={apiRef}
        containerSx={{ flex: 1, minHeight: 0, width: "100%" }}
        enableQuickFilterHotkey={false}
      />
    </ConsoleLogsTableWrapper>
  );
};

const forceMoveToTimestampDeviceTabAtom = atom<null | number>(null);

const ConsoleLogsTableWrapper = materialStyled(Box)({
  display: "flex",
  flexDirection: "column",
  gridArea: "tab-content",
  position: "relative",
  backgroundColor: "#232323",
  boxShadow:
    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
  borderRadius: "4px",
  padding: 0,
  margin: "16px",
});

export { forceMoveToTimestampDeviceTabAtom };

export default ConsoleTab;
